'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';

import { useAuth, useSignInHandlers } from '@/hooks';
import { referralApi } from '@/services/api/referral.api';
import { setAuthModalLocked, useAppDispatch } from '@/store';

const REFERRAL_STORAGE_KEY = 'pm_referral_code';
const REFERRAL_EXPIRY_DAYS = 30;

/**
 * Resolves whether a code is a genuine admin Ambassador batch-invite (as
 * opposed to a regular user's personal referral code). Best-effort: on any
 * validation failure we fail open (treat as a normal, dismissible referral)
 * rather than risk trapping a user in a non-dismissible modal.
 */
async function resolveIsAmbassadorInvite(code: string): Promise<boolean> {
  try {
    const result = await referralApi.validateReferralCode(code);
    return Boolean(result?.isAmbassadorInvite);
  } catch (e) {
    console.warn('[ReferralTracker] Failed to validate referral code:', e);
    return false;
  }
}

/**
 * ReferralTracker Component
 * Headless component that listens for referral codes in the URL and persists them.
 * Supported params: 'r', 'referral', 'ref'
 */
export function ReferralTracker() {
  const searchParams = useSearchParams();
  const { isAuthenticated, isLoading } = useAuth();
  const dispatch = useAppDispatch();
  const triggerRef = useRef<string | null>(null);
  const timersRef = useRef<{ login?: NodeJS.Timeout; reset?: NodeJS.Timeout }>({});
  // Ensures the auth modal is auto-opened exactly once per captured code.
  const hasTriggeredLoginRef = useRef(false);

  const { onLogin } = useSignInHandlers((error) => {
    if (error) console.warn('[ReferralTracker] Auto-login error handled:', error);
  });

  const processReferral = useCallback(() => {
    if (!searchParams) return;

    const r = searchParams.get('r');
    const referral = searchParams.get('referral');
    const ref = searchParams.get('ref');

    const code = r || referral || ref;

    // Security & Format Validation: Ensure code is safe and valid
    if (code && typeof code === 'string' && triggerRef.current !== code) {
      const VALID_CODE_REGEX = /^[a-zA-Z0-9_-]+$/;

      if (code.length > 50 || !VALID_CODE_REGEX.test(code)) {
        console.warn(
          '[ReferralTracker] Discarding invalid or potentially malicious referral code:',
          code,
        );
        return;
      }

      triggerRef.current = code;

      // Clean up the URL parameters immediately for all users to maintain a clean address bar
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('r');
        url.searchParams.delete('referral');
        url.searchParams.delete('ref');
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        console.warn('[ReferralTracker] URL cleanup failed:', e);
      }

      // If the user is already logged in, we do not process the referral further.
      // This ensures no UI side-effects or storage updates occur for active sessions.
      if (isAuthenticated && !isLoading) {
        return;
      }

      try {
        // Save to localStorage with a timestamp for expiration checking
        const referralData = {
          code,
          timestamp: Date.now(),
          expiresAt: Date.now() + REFERRAL_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
        };

        localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referralData));

        // Automatically open login if not already logged in
        if (!isLoading && !isAuthenticated) {
          timersRef.current.login = setTimeout(() => {
            onLogin();
          }, 1500);
        }
      } catch (err) {
        console.error('[ReferralTracker] Failed to persist referral code:', err);
      } finally {
        // Reset trigger after a delay to allow re-processing if they click again later
        timersRef.current.reset = setTimeout(() => {
          triggerRef.current = null;
        }, 10000);
      }
    }
  }, [searchParams, isAuthenticated, isLoading, onLogin]);

  useEffect(() => {
    // Stage 1: Persistence
    // We process the code as soon as it's seen in the URL
    const r = searchParams.get('r');
    const referral = searchParams.get('referral');
    const ref = searchParams.get('ref');
    const code = r || referral || ref;

    let cancelled = false;

    if (code && typeof code === 'string' && triggerRef.current !== code) {
      const VALID_CODE_REGEX = /^[a-zA-Z0-9_-]+$/;
      if (code.length <= 50 && VALID_CODE_REGEX.test(code)) {
        triggerRef.current = code;
        // A freshly-seen code should be able to re-open the login modal.
        hasTriggeredLoginRef.current = false;

        // Persist immediately (Ambassador status is patched in once resolved
        // below), so the code survives a reload even mid-validation.
        localStorage.setItem(
          REFERRAL_STORAGE_KEY,
          JSON.stringify({
            code,
            timestamp: Date.now(),
            expiresAt: Date.now() + REFERRAL_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
            isAmbassadorInvite: false,
          }),
        );

        // Clean up URL
        try {
          const url = new URL(window.location.href);
          ['r', 'referral', 'ref'].forEach((p) => url.searchParams.delete(p));
          window.history.replaceState({}, '', url.toString());
        } catch (e) {
          console.error(e);
        }

        // Reset trigger ref after delay
        timersRef.current.reset = setTimeout(() => {
          triggerRef.current = null;
        }, 10000);

        // Resolve whether this is a genuine Ambassador batch-invite (vs. a
        // regular user's personal referral code) before opening the login
        // modal, so it opens already in the correct locked/unlocked state —
        // opening unlocked first and locking a moment later would let the
        // user close it during that gap.
        resolveIsAmbassadorInvite(code).then((isAmbassadorInvite) => {
          if (cancelled) return;

          try {
            const raw = localStorage.getItem(REFERRAL_STORAGE_KEY);
            const data = raw ? JSON.parse(raw) : null;
            if (data && data.code === code) {
              localStorage.setItem(
                REFERRAL_STORAGE_KEY,
                JSON.stringify({ ...data, isAmbassadorInvite }),
              );
            }
          } catch (e) {
            console.error('[ReferralTracker] Failed to persist ambassador flag:', e);
          }

          if (!isLoading && !isAuthenticated && !hasTriggeredLoginRef.current) {
            hasTriggeredLoginRef.current = true;
            dispatch(setAuthModalLocked(isAmbassadorInvite));
            timersRef.current.login = setTimeout(() => {
              onLogin();
            }, 1200);
          }
        });
      }
    } else if (!isLoading && !isAuthenticated && !hasTriggeredLoginRef.current) {
      // Stage 2: Login Trigger for a code persisted from an earlier visit
      // (e.g. the user reloaded/returned before completing login). A code
      // freshly seen in the URL this render is handled above, once its
      // Ambassador status resolves.
      const persistedCode = getPersistedReferralCode();
      if (persistedCode) {
        hasTriggeredLoginRef.current = true;
        dispatch(setAuthModalLocked(getPersistedReferralIsAmbassador()));
        timersRef.current.login = setTimeout(() => {
          onLogin();
        }, 1200);
      }
    }

    return () => {
      cancelled = true;
      if (timersRef.current.reset) clearTimeout(timersRef.current.reset);
    };
  }, [searchParams, isAuthenticated, isLoading, onLogin, dispatch]);

  return null;
}

/**
 * Utility to get the current valid referral code
 */
export function getPersistedReferralCode(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);

    // Structural validation
    if (!data || typeof data !== 'object' || !data.code) {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }

    // Expiry check
    if (data.expiresAt && Date.now() > data.expiresAt) {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }
    return data.code;
  } catch (e) {
    console.error('[ReferralTracker] Error reading referral code:', e);
    return null;
  }
}

/**
 * Utility to check whether the persisted referral code is a genuine admin
 * Ambassador batch-invite (as opposed to a regular user's personal referral
 * code). Defaults to false if unresolved, malformed, or absent.
 */
export function getPersistedReferralIsAmbassador(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const raw = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return false;

    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return false;
    if (data.expiresAt && Date.now() > data.expiresAt) return false;

    return Boolean(data.isAmbassadorInvite);
  } catch (e) {
    console.error('[ReferralTracker] Error reading ambassador flag:', e);
    return false;
  }
}

/**
 * Utility to clear the referral code after successful signup
 */
export function clearPersistedReferralCode() {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
    } catch (e) {
      console.error('[ReferralTracker] Error clearing referral code:', e);
    }
  }
}
